import { createContext, useContext, useEffect, useState } from "react";

const PermissionCtx = createContext({
  perms: new Set(),
  ready: false,
  reload: () => {},
});

/** Standard-Header-Auflösung: Keycloak-Token ODER internes JWT.
 *  Kann durch die `fetchHeaders` Prop überschrieben werden. */
function defaultFetchHeaders() {
  // 1) Keycloak (gesetzt durch AuthContext)
  if (typeof window !== "undefined" && window._kcToken) {
    return { Authorization: `Bearer ${window._kcToken}` };
  }
  // 2) Internal Auth – probiere gängige Storage-Keys
  if (typeof window !== "undefined" && window.localStorage) {
    for (const k of ["internal_token", "token", "access_token", "auth_token", "jwt", "ffw_token"]) {
      const t = window.localStorage.getItem(k);
      if (t) return { Authorization: `Bearer ${t}` };
    }
  }
  // 3) Letzter Fallback: Cookie wird automatisch vom Browser mitgesendet
  return {};
}

export function PermissionProvider({ children, fetchHeaders }) {
  const [perms, setPerms] = useState(new Set());
  const [ready, setReady] = useState(false);

  const load = async () => {
    try {
      const headers = (fetchHeaders || defaultFetchHeaders)();
      const r = await fetch("/api/me/berechtigungen", {
        headers,
        credentials: "include",  // Cookie-basiertes Auth abdecken
      });
      if (r.ok) {
        const arr = await r.json();
        setPerms(new Set(arr));
      } else {
        setPerms(new Set());
      }
    } catch {
      setPerms(new Set());
    } finally {
      setReady(true);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <PermissionCtx.Provider value={{ perms, ready, reload: load }}>
      {children}
    </PermissionCtx.Provider>
  );
}

export function usePermissions() { return useContext(PermissionCtx); }
export function useHasPerm(key)  { return useContext(PermissionCtx).perms.has(key); }
export function useHasAny(...keys) {
  const { perms } = useContext(PermissionCtx);
  return keys.some(k => perms.has(k));
}
export function Gate({ perm, anyOf, fallback = null, children }) {
  const { perms } = useContext(PermissionCtx);
  const ok = perm ? perms.has(perm) : (anyOf || []).some(k => perms.has(k));
  return ok ? children : fallback;
}
